import type { TourStepDefinition } from '@dev_mat/floating-ui-tour';
import { registerTourPage } from '@/composables/tour/tourRegistry';
import { isElementVisible, step } from '@/composables/tour/stepHelpers';

type BuilderTab = 'details' | 'fields' | 'branding';

const activateBuilderTab = (tab: BuilderTab) => {
  window.dispatchEvent(new CustomEvent<BuilderTab>('form-builder:activate-tab', { detail: tab }));
};

/**
 * Builder steps live inside TabView panels that stay mounted but hidden
 * (v-show) while another tab is active — `step()`'s hidden-target skip would
 * silently drop them. Before the visibility check we dispatch an event the
 * builder listens for to switch tabs; the DOM flushes on the next microtask
 * (before the tour's async positioning), so a still-hidden-but-connected
 * target must not be skipped here.
 */
const stepInTab = (tab: BuilderTab, def: TourStepDefinition): TourStepDefinition => {
  const base = step(def);
  return {
    ...base,
    onBeforeShow: (s, i, tour) => {
      activateBuilderTab(tab);
      if (!isElementVisible(s.target)) {
        const el = typeof s.target === 'string' ? document.querySelector(s.target) : s.target;
        if (!(el instanceof HTMLElement) || !el.isConnected) return false;
      }
      return def.onBeforeShow ? def.onBeforeShow(s, i, tour) : undefined;
    },
  };
};

registerTourPage('admin-forms', {
  key: 'admin-forms',
  label: 'Forms Management',
  getSteps: () => [
    step({
      target: '#tour-forms-type-filter',
      title: 'Filter by Type',
      content:
        'Switch between General forms (surveys, feedback, etc.) and Admission forms (which feed into the applicant pipeline and convert into students).',
      placement: 'bottom',
    }),
    step({
      target: '#tour-forms-create-btn',
      title: 'Create a Form',
      content:
        'Start a new form. You\'ll get a builder with a metadata block, drag-and-drop fields, and visibility controls.',
      placement: 'left',
    }),
    step({
      target: '#tour-forms-table',
      title: 'Your Forms',
      content:
        'Each row shows a form, its type, public/private status, and how many submissions it has. Use the action buttons to edit, view submissions, or delete a form.',
      placement: 'top',
    }),
  ],
});

registerTourPage('admin-forms-create', {
  key: 'admin-forms-create',
  label: 'Form Builder',
  getSteps: () => [
    step({
      target: '#tour-form-builder-actions',
      title: 'Form Actions',
      content:
        '"Live Preview" opens the form as your respondents will see it. Save from the bar at the bottom of the '
        + 'page to create a draft you can keep iterating on, then flip "Active" + "Public" to publish.',
      placement: 'bottom',
    }),
    stepInTab('details', {
      target: '#tour-form-builder-meta',
      title: 'Form Details',
      content:
        'Title, type, and visibility. Mark a form Public for a shareable link, restrict it to logged-in '
        + 'students, or pick Admission to enable applicant review and student conversion.',
      placement: 'bottom',
    }),
    stepInTab('fields', {
      target: '#tour-form-builder-toolbox',
      title: 'Add Fields',
      content:
        'Click any button here to drop a field into the form. Fields can be reordered with the arrows on each row.',
      placement: 'left',
    }),
    stepInTab('fields', {
      target: '#tour-form-builder-fields',
      title: 'Configure Each Field',
      content:
        'Each row summarises a field — click it to open the full editor: change the label, mark it required, '
        + 'set choices for dropdowns, etc. For admission forms you can also pre-map fields to student record columns.',
      placement: 'top',
    }),
  ],
});

registerTourPage('admin-forms-edit', {
  key: 'admin-forms-edit',
  label: 'Edit Form',
  getSteps: () => [
    step({
      target: '#tour-form-builder-actions',
      title: 'Edit Mode',
      content:
        'You\'re editing an existing form. Changes apply once you save from the bar at the bottom of the page, and the share link stays the same — no need to redistribute it.',
      placement: 'bottom',
    }),
  ],
});

registerTourPage('admin-forms-submissions', {
  key: 'admin-forms-submissions',
  label: 'Form Submissions',
  getSteps: () => [
    step({
      target: '#tour-form-submissions-actions',
      title: 'Toolbar',
      content:
        '"Edit Form" jumps back to the builder, "Export CSV" downloads everything to a spreadsheet. For admission forms you also get status-filter buttons up top.',
      placement: 'bottom',
    }),
    step({
      target: '#tour-form-submissions-table',
      title: 'Submission List',
      content:
        'Each row is one submission. Click in to see the full answers. For admission forms, select rows and '
        + 'use the bulk bar to approve, reject, or convert applicants to students in one go.',
      placement: 'top',
    }),
  ],
});
